import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { Point } from "../interfaces/point";
import { Rectangle } from "../interfaces/textLayer";
import useMouse from "@react-hook/mouse-position";
import {
  calculateSelectionRectangle,
  getMinimalCoords,
} from "../helpers/selectionHelpers";
import { buildNerAnnotation } from "../helpers/annotationHelpers";
import useKeyPressedListener from "./useKeyPressedListener";
import { useAppDispatch, useAppSelector } from "../../app";
import useAnnotationUpdater from "./useAnnotationUpdater";
import { setIsDragging } from "../../annotation/store/annotationSlice";
import { AnnotationParams } from "../interfaces/annotation";
import textLayerForPageSelector from "../../annotation/selectors/textlayerForPageSelector";
import { EAnnotatorMode } from "../../annotation/constants";
import documentSetDisabledSelector from "../../documentSet/selectors/documentSetDisabledSelector";
import { useTranslation } from "react-i18next";
import activeEntityForAnnotatorSelector from "../selectors/activeEntityForAnnotatorSelector";

const initialCoords: Rectangle = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  rotation: 0,
};

interface Props {
  pageNumber: number;
  ref: RefObject<HTMLDivElement | null>;
}

const useSelection = ({ ref, pageNumber }: Props) => {
  const dispatch = useAppDispatch();

  const {
    i18n: { language },
  } = useTranslation();

  const disabled = useAppSelector(documentSetDisabledSelector);
  const configMap = useAppSelector((state) => state.configMapReducer.configMap);
  const { isDragging } = useAppSelector((state) => state.annotationReducer);
  const entity = useAppSelector((state) =>
    activeEntityForAnnotatorSelector(state, { language }),
  );
  const textLayerForPage = useAppSelector((state) =>
    textLayerForPageSelector(state, {
      page: pageNumber,
      excludeTokensOfEntityId: entity?.id ?? null,
      excludeTokensOfIndex: entity?.index,
    }),
  );

  const [mouseCoords, setMouseCoords] = useState<Point>({ x: 0, y: 0 });
  const [coords, setCoords] = useState(initialCoords);

  const { addAnnotation, updateLastAnnotationForEntity } =
    useAnnotationUpdater();

  const keyPressed = useKeyPressedListener();

  // @ts-expect-error `useMouse` predates React 19's `RefObject<T | null>`, so it
  // accepts only `RefObject<HTMLElement>` and rejects the nullable ref type that
  // `useRef<HTMLDivElement>(null)` now produces.
  const mouse = useMouse(ref);

  const annotatorMode = useMemo((): EAnnotatorMode => {
    if (entity) {
      return EAnnotatorMode.ANNOTATION_MODE;
    }

    return EAnnotatorMode.TEXT_SELECTION_MODE;
  }, [entity]);

  const mousePosition = useMemo((): Point => {
    const { x, y } = mouse;
    if (!x || !y) {
      return { x: 0, y: 0 };
    }

    return { x, y };
  }, [mouse]);

  const handleMouseDown = useCallback(() => {
    const { x, y } = mouse;
    if (entity) {
      setMouseCoords({ x: x!, y: y! });
      dispatch(setIsDragging(true));
    }
  }, [entity, mouse, dispatch]);

  const createNerAnnotation = useCallback(
    (coords: Rectangle): AnnotationParams | null => {
      if (!textLayerForPage || !entity) {
        return null;
      }

      const { tokens, text } = textLayerForPage;

      const groupBlock = configMap?.groupBlocks?.find((gb) =>
        gb.groupBlockEntityTypes
          .map((gbet) => gbet.entityType.id)
          .includes(entity.id),
      );

      const markToAdd = buildNerAnnotation(
        pageNumber,
        entity,
        tokens,
        text,
        coords,
        entity.multipleGroupBlocks,
        groupBlock?.id,
      );

      if (!markToAdd?.pageTokenIndices?.length) {
        return null;
      }

      return markToAdd;
    },
    [textLayerForPage, entity, pageNumber, configMap?.groupBlocks],
  );

  const handleNerAnnotation = useCallback(
    (coords: Rectangle) => {
      const markToAdd = createNerAnnotation(coords);

      if (!markToAdd) {
        return;
      }

      if (keyPressed) {
        updateLastAnnotationForEntity(markToAdd);
      } else {
        addAnnotation(markToAdd);
      }
    },
    [
      keyPressed,
      updateLastAnnotationForEntity,
      addAnnotation,
      createNerAnnotation,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (disabled) {
      return;
    }

    const coordsToUse = getMinimalCoords(coords, mousePosition);

    switch (annotatorMode) {
      case EAnnotatorMode.ANNOTATION_MODE:
        handleNerAnnotation(coordsToUse);
        break;
      default:
        break;
    }

    dispatch(setIsDragging(false));
    setMouseCoords({ x: 0, y: 0 });
    setCoords(initialCoords);
  }, [
    disabled,
    dispatch,
    annotatorMode,
    coords,
    mousePosition,
    handleNerAnnotation,
  ]);

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event?.key?.toLowerCase() === "escape" && isDragging) {
        dispatch(setIsDragging(false));
        setMouseCoords({ x: 0, y: 0 });
        setCoords(initialCoords);
      }
    },
    [isDragging, dispatch],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyEvent, false);
    return () => {
      document.removeEventListener("keydown", handleKeyEvent, false);
    };
  }, [handleKeyEvent]);

  const handleMouseMove = useCallback(() => {
    if (isDragging && entity) {
      const { x, y } = mousePosition;
      setCoords(calculateSelectionRectangle(mouseCoords, { x: x, y: y }));
    }
  }, [isDragging, entity, mouseCoords, mousePosition]);

  return {
    annotatorMode,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    mousePosition,
    coords,
    isDragging,
  };
};

export default useSelection;
